'use client';

import { useState } from 'react';

import { Check, Copy, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@app/shadcn/components/ui/button';
import { useToast } from '@app/shadcn/components/ui/use-toast';
import { selectForm } from '@app/store/forms/slice';
import { useAppSelector } from '@app/store/hooks';
import { selectWorkspace } from '@app/store/workspaces/slice';
import getFormShareURL from '@app/utils/form-utils';

/**
 * Post-publish moment, on the trust system and matching the Share modal's
 * idiom: one calm confirmation, the link front-and-centre with copy/open,
 * and honest next steps as quiet links — no double celebration, no dead-end
 * "go to dashboard" as the only exit.
 */
export default function FormPublishedModal(props: any) {
    const { t } = useTranslation('builder');
    const { toast } = useToast();
    const workspace = useAppSelector(selectWorkspace);
    const standardForm = useAppSelector(selectForm);
    const [copied, setCopied] = useState(false);

    const shareUrl = getFormShareURL(standardForm, workspace);
    const settingsUrl = `${window.PUBLIC_CONFIG?.HTTP_SCHEME}${window.PUBLIC_CONFIG?.DASHBOARD_DOMAIN}/${workspace.workspaceName}/dashboard/forms/${standardForm.formId}?view=FormLinks`;
    const dashboardUrl = `${window.PUBLIC_CONFIG?.HTTP_SCHEME}${window.PUBLIC_CONFIG?.DASHBOARD_DOMAIN}/${workspace.workspaceName}/dashboard/forms`;

    const handleCopy = () => {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast({ description: t('PUBLISHED_MODAL.COPIED_TOAST') });
        setTimeout(() => setCopied(false), 2000);
    };

    // min-w-0 on the root matters: DialogContent is display:grid, and a grid
    // item's implicit min-width:auto lets the unbreakable URL dictate the
    // width — overflowing the dialog instead of truncating.
    return (
        <div className="flex w-full min-w-0 max-w-full flex-col gap-6 overflow-hidden p-6 sm:p-8">
            <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2.5">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E7F4EE]">
                        <Check className="h-4 w-4 text-success" strokeWidth={3} />
                    </span>
                    <h2 className="text-black-900 text-lg font-semibold leading-snug">{t('PUBLISHED_MODAL.TITLE')}</h2>
                </div>
                {/* Honest about what publishing means — same line the Share modal uses. */}
                <p className="text-black-600 text-sm leading-relaxed">{t('PUBLISHED_MODAL.BODY')}</p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="border-black-300 bg-black-100 flex min-w-0 flex-1 items-center gap-2 rounded-lg border px-3.5 py-3">
                    <span className="text-black-800 min-w-0 flex-1 truncate font-mono text-[13px]" title={shareUrl}>
                        {shareUrl}
                    </span>
                    <a
                        href={shareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t('PUBLISHED_MODAL.OPEN_NEW_TAB')}
                        data-umami-event={'PublishModal Open Form Link'}
                       
                        className="text-black-600 hover:text-brand-500 shrink-0 transition-colors"
                    >
                        <ExternalLink className="h-4 w-4" />
                    </a>
                </div>
                <Button
                    size="medium"
                    variant="primary"
                    onClick={handleCopy}
                    icon={copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    className="shrink-0"
                    data-umami-event={'PublishModal Copy Button'}
                   
                >
                    {copied ? t('PUBLISHED_MODAL.COPIED') : t('PUBLISHED_MODAL.COPY')}
                </Button>
            </div>

            <div className="border-t-black-300 flex flex-col gap-2.5 border-t pt-5">
                <span className="text-black-600 text-xs font-medium uppercase tracking-wide">{t('PUBLISHED_MODAL.NEXT_STEPS')}</span>
                <div className="text-black-700 flex flex-col gap-2 text-sm">
                    <a href={settingsUrl} data-umami-event={'PublishModal Goto Settings Link'} className="hover:text-brand-600 text-brand-500 w-fit font-medium underline-offset-2 hover:underline">
                        {t('PUBLISHED_MODAL.SETTINGS')}
                    </a>
                    <a href={dashboardUrl} data-umami-event={'PublishModal Goto Dashboard Link'} className="hover:text-brand-600 text-brand-500 w-fit font-medium underline-offset-2 hover:underline">
                        {t('PUBLISHED_MODAL.DASHBOARD')}
                    </a>
                </div>
            </div>
        </div>
    );
}
